import React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { AppUI } from '@seedlang/state';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import Button from 'components/button/button';
import Paginator from 'components/paginator';
import Spinner from 'components/spinner';
import Text from 'components/text';
import TextDisplay from 'components/text_display';
import HeaderBar from 'components/header_bar';
import CommentIndex from 'components/comment/comment_index';
import { Theme } from '@seedlang/constants';

const BLOG_FORUM_ID = '9332f9ec-35ac-453d-aab3-fec5d814e300';

const Wrapper = styled.div``;

const PostList = styled.div`
  margin: 0;
`;

const PostCard = styled.article`
  background: white;
  border: 1px solid #dedede;
  border-radius: 10px;
  padding: 24px;
  margin: 0 0 24px 0;
  position: relative;
`;

const UnpublishedLabel = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  color: ${Theme.darkGray};
  background: ${Theme.lightGray};
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 5px;
`;

const PostHeader = styled.div`
  margin: 0 0 18px 0;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin: 8px 0 0 0;
`;

const CommentsSection = styled.div`
  margin: 24px 0 0 0;
  padding: 20px 0 0 0;
  border-top: 1px solid #dedede;
`;

const CommentToggle = styled.button`
  background: none;
  border: 0;
  color: #333;
  cursor: pointer;
  display: block;
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  margin: ${props => props.expanded ? '0 0 18px 0' : '0'};
  padding: 0;
  text-decoration: underline;
`;

const SpinnerWrapper = styled.div`
  margin: 30px 0;
`;

@observer
class Blog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      expandedCommentThreadIds: {},
    };
    AppUI.loadUser();
    AppUI.siteReleaseStore.clearFilter();
    AppUI.siteReleaseStore.setPage(1);
    AppUI.siteReleaseStore.getIndex({
      filters: {
        published: 'true',
      },
      limit: 10,
      queryStrings: {
        hide_hidden_forum_threads: true,
      },
      sort: '-created_at',
    }, this.afterGetIndex);
  }

  afterGetIndex = (resp) => {
    AppUI.siteReleaseStore.setIndexData(resp);
  }

  toggleComments = (forumThreadId) => {
    this.setState(prevState => ({
      expandedCommentThreadIds: {
        ...prevState.expandedCommentThreadIds,
        [forumThreadId]: !prevState.expandedCommentThreadIds[forumThreadId],
      },
    }));
  }

  render() {
    return (
      <Wrapper>
        <HeaderBar
          title='Blog'
          contentGap={6}
          titleRoute='blog'
        />
        {
          AppUI.siteReleaseStore.hasOutstandingRequests && AppUI.siteReleaseStore.indexData.length === 0 &&
            <SpinnerWrapper>
              <Spinner />
            </SpinnerWrapper>
        }
        <PostList>
          {
            AppUI.siteReleaseStore.indexData.map(item => {
              const commentsAreExpanded = Boolean(this.state.expandedCommentThreadIds[item.forumThreadId]);
              const body = item.body || item.message;
              const commentCount = item.commentsCount || 0;
              const commentLabel = `${commentCount} ${commentCount === 1 ? 'Comment' : 'Comments'}`;

              return (
                <PostCard key={item.id}>
                  {
                    !item.published &&
                      <UnpublishedLabel>
                        Unpublished
                      </UnpublishedLabel>
                  }
                  <PostHeader>
                    <Text heading={3}>{item.title}</Text>
                    <MetaRow>
                      <Text color='#7a7a7a' fontSize='14px' lineHeight='18px'>
                        {item.createdAt?.formattedDateWithFullYear || item.createdAt?.formattedDate}
                      </Text>
                    </MetaRow>
                  </PostHeader>

                  {
                    body &&
                      <TextDisplay markdown={body} />
                  }

                  <CommentsSection>
                    {
                      item.forumThreadId && commentCount === 0 &&
                        <Text color='#7a7a7a'>
                          No comments yet.
                        </Text>
                    }
                    {
                      commentCount > 0 &&
                        <CommentToggle
                          expanded={commentsAreExpanded}
                          onClick={() => this.toggleComments(item.forumThreadId)}
                        >
                          {commentsAreExpanded ? 'Hide Comments' : `Show ${commentLabel}`}
                        </CommentToggle>
                    }
                    {
                      commentsAreExpanded &&
                        <CommentIndex
                          hideUserProfile={AppUI.siteIsMemrev}
                          showCommentCount={false}
                          showCommentCreate={false}
                          user={AppUI.user}
                          commentableType='ForumThread'
                          commentableId={item.forumThreadId}
                          forumThreadId={item.forumThreadId}
                          commentMargin='0'
                          margin='0'
                          padding='0'
                          indentation={20}
                          compactCommentLayout
                          hideCommentControls
                        />
                    }
                    {
                      item.forumThreadId && (commentCount === 0 || commentsAreExpanded) &&
                        <Button
                          to={{name: 'forums.forum_threads.show', params: {forumId: BLOG_FORUM_ID, forumThreadId: item.forumThreadId}}}
                          margin='14px 0 0 0'
                          height='40px'
                          fontSize='15px'
                          >
                          Leave Comment
                        </Button>
                    }
                  </CommentsSection>
                </PostCard>
              );
            })
          }
        </PostList>
        <Paginator
          store={AppUI.siteReleaseStore}
          queryStrings={{
            hide_hidden_forum_threads: true,
          }}
        />
      </Wrapper>
    );
  }
}

export default ErrorBoundary(Blog);
